import {
  CompletionRequest,
  DefinitionRequest,
  DidChangeTextDocumentNotification,
  DidCloseTextDocumentNotification,
  DidOpenTextDocumentNotification,
  HoverRequest,
  InitializeRequest,
  InitializedNotification,
  LogMessageNotification,
  type NotificationMessage,
  PublishDiagnosticsNotification,
  type RequestMessage,
  type ResponseMessage,
  SemanticTokensRequest,
  TelemetryEventNotification,
} from 'vscode-languageserver-protocol/browser';

import {
  CanComposeNotification,
  ConfigureAutoComposition,
  RequestCompositionRequest,
} from './customEvents';

const handledPostMessage = [
  InitializeRequest,
  InitializedNotification,
  DidOpenTextDocumentNotification,
  DidChangeTextDocumentNotification,
  DidCloseTextDocumentNotification,
  CompletionRequest,
  HoverRequest,
  SemanticTokensRequest,
  RequestCompositionRequest,
  ConfigureAutoComposition,
  DefinitionRequest,
];
const handledReceivedMessages = [
  PublishDiagnosticsNotification,
  LogMessageNotification,
  TelemetryEventNotification,
  CanComposeNotification,
  { method: '$/progress' },
];
function setupDebugLogging(worker: Worker) {
  const sentIdMap = new Map<string | number, string>();
  const originalPostMessage = worker.postMessage.bind(worker);
  // eslint-disable-next-line no-param-reassign
  worker.postMessage = (
    message: NotificationMessage | RequestMessage,
    transferable,
  ) => {
    // eslint-disable-next-line no-console
    console.log('message sent', message, transferable);
    if (
      handledPostMessage.every(
        (allowedMessage) => allowedMessage.method !== message.method,
      )
    ) {
      throw new Error(
        `implement this message in monaco version: ${message.method}`,
      );
    }
    // types were causing problems here, but transferable is never actually passed in. ignore it, but throw if that changes.
    if (transferable) {
      throw new Error('transferable passed in but not handled');
    }
    if ('id' in message && message.id !== null) {
      sentIdMap.set(message.id, message.method);
    }

    return originalPostMessage(message);
  };

  worker.addEventListener(
    'message',
    (
      event: MessageEvent<
        | ResponseMessage
        | NotificationMessage
        | RequestMessage
        | 'serverWorkerReady'
      >,
    ) => {
      const message = event.data;
      if (message === 'serverWorkerReady') {
        return;
      }

      if ('method' in message) {
        // eslint-disable-next-line no-console
        console.log('message received: ', message);
        if (
          handledReceivedMessages.every(
            (allowedMessage) => allowedMessage.method !== message.method,
          )
        ) {
          throw new Error(
            `implement this message in monaco version: ${message.method}`,
          );
        }
      } else if (message.id !== null) {
        // eslint-disable-next-line no-console
        console.log(
          `message response for ${sentIdMap.get(message.id)}: `,
          message,
        );
        sentIdMap.delete(message.id);
      } else {
        // eslint-disable-next-line no-console
        console.log(`message response: `, message);
      }
    },
  );
}
export function loadWorker(
  version: `${number}.${number}.${number}` | 'local',
  enableDebugLogging: boolean,
  handleTimeout: (reject: () => void) => void,
): Promise<Worker> {
  const USE_DEPLOYED_VERSION = version === 'local' ? null : version;
  const languageServerDomain = USE_DEPLOYED_VERSION
    ? `https://language-server-workers.cdn.apollographql.com/`
    : 'https://localhost:8081/';
  const languageServerWorkerFile = `${
    USE_DEPLOYED_VERSION ? `${USE_DEPLOYED_VERSION}/` : ''
  }language-server.worker.js`;

  // Language Server preparation
  const worker = new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `importScripts('${
            new URL(languageServerWorkerFile, languageServerDomain).href
          }')`,
        ],
        { type: 'application/javascript' },
      ),
    ),
    { name: 'Apollo Language Server Worker' },
  );
  if (enableDebugLogging) {
    setupDebugLogging(worker);
  }

  return new Promise<Worker>((resolve, reject) => {
    const timeoutId = setTimeout(handleTimeout, 10000, reject);
    worker.onmessage = (event) => {
      if (event.data === 'serverWorkerReady') {
        clearTimeout(timeoutId);
        resolve(worker);
      }
    };
  });
}
